import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import {
  buildTiktokChildPath,
  buildTiktokPath,
  buildTiktokProductChildPath,
  normalizeTiktokShopTypeSlug,
} from '@/tiktok-platform/constants/tiktokRoutes';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

const ANALYSIS_PRODUCT_METRICS = [
  { key: 'listed', labelKey: 'platformHome.listed', tone: 'ok', path: '' },
  { key: 'pending', labelKey: 'platformHome.pendingList', tone: 'warn', path: '' },
  { key: 'soldOut', labelKey: 'platformHome.soldOut', tone: 'muted', path: '' },
  { key: 'offline', labelKey: 'platformHome.offline', tone: 'muted', path: '' },
  { key: 'drafts', labelKey: 'platformHome.draft', tone: 'info', path: 'drafts' },
];

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function AnalysisBar({ label, value, max, tone = 'info', onClick, disabled }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <button
      type="button"
      className={`tko-home-bar tko-home-bar--${tone}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="tko-home-bar__meta">
        <span className="tko-home-bar__label">{label}</span>
        <span className="tko-home-bar__value">{value}</span>
      </span>
      <span className="tko-home-bar__track" aria-hidden>
        <span className="tko-home-bar__fill" style={{ width: `${pct}%` }} />
      </span>
    </button>
  );
}

export default function TiktokHomePage() {
  const { t } = useTranslation('workbench');
  const {
    scopedShops = [],
    concreteScopeId,
    allShopsScope,
    listReady,
    shopDimensionTabs,
  } = useTiktokPageShopScope();
  const { shopType } = useParams();
  const slug = normalizeTiktokShopTypeSlug(shopType) || 'semi';
  const isFull = slug === 'full';
  const shopId = concreteScopeId || '';
  const scopedShopIds = useMemo(
    () => (scopedShops || []).map((s) => s?.id).filter((id) => id != null).map(String),
    [scopedShops],
  );
  const navigate = useNavigate();

  const [listingStats, setListingStats] = useState(null);
  const [todos, setTodos] = useState({ awaitingShipment: 0, pendingReturns: 0 });
  const [loading, setLoading] = useState(false);
  const loadSeqRef = useRef(0);

  const loadForShop = useCallback(async (id) => {
    const statsRes = await tiktokPlatformAPI.getProductListingStats(id).catch(() => null);
    return { listingStats: statsRes || null };
  }, []);

  const loadLocalHome = useCallback(async ({ silent = false } = {}) => {
    if (!listReady) {
      setListingStats(null);
      return;
    }
    const seq = ++loadSeqRef.current;
    if (!silent) setLoading(true);
    try {
      if (allShopsScope) {
        const rows = await Promise.all(scopedShopIds.map((id) => loadForShop(id)));
        if (seq !== loadSeqRef.current) return;
        const sumStats = {};
        rows.forEach((row) => {
          const stats = row.listingStats || {};
          Object.keys(stats).forEach((key) => {
            if (typeof stats[key] === 'number' || Number.isFinite(Number(stats[key]))) {
              sumStats[key] = num(sumStats[key]) + num(stats[key]);
            }
          });
        });
        setListingStats(Object.keys(sumStats).length ? sumStats : null);
      } else {
        const row = await loadForShop(shopId);
        if (seq !== loadSeqRef.current) return;
        setListingStats(row.listingStats);
        if (shopId) {
          const [orders, returns] = await Promise.all([
            tiktokPlatformAPI.listTiktokOrders(shopId, { page: 1, pageSize: 1, orderStatus: 'AWAITING_SHIPMENT' }).catch(() => null),
            tiktokPlatformAPI.listReturnOrders(shopId, { pageSize: 1, returnStatus: 'RETURN_OR_REFUND_REQUEST_PENDING' }).catch(() => null),
          ]);
          if (seq !== loadSeqRef.current) return;
          setTodos({
            awaitingShipment: num(orders?.totalElements ?? orders?.total),
            pendingReturns: num(returns?.totalElements ?? returns?.total),
          });
        }
      }
    } catch (e) {
      if (!silent) showToast(e?.message || t('platformHome.loadFailed'), 'error');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [listReady, allShopsScope, scopedShopIds, shopId, loadForShop, t]);

  useEffect(() => {
    loadLocalHome();
  }, [loadLocalHome]);

  const goProductMetric = (metric) => {
    if (metric.path === 'drafts') {
      navigate(buildTiktokProductChildPath('drafts'));
      return;
    }
    navigate(buildTiktokProductChildPath(''));
  };

  const productTotal = useMemo(() => {
    if (listingStats?.total != null) return num(listingStats.total);
    return ANALYSIS_PRODUCT_METRICS.reduce((sum, m) => sum + num(listingStats?.[m.key]), 0);
  }, [listingStats]);

  const productMax = useMemo(() => (
    Math.max(1, ...ANALYSIS_PRODUCT_METRICS.map((m) => num(listingStats?.[m.key])))
  ), [listingStats]);

  return (
    <ProductListCard tabs={shopDimensionTabs}>
      <div className="tko-home">
        <section className="tko-home-block tko-home-section" aria-labelledby="tko-home-analysis-title">
          <div className="tko-home-section__head">
            <h2 id="tko-home-analysis-title" className="tko-home-section__title">{t('platformHome.analysis')}</h2>
            {allShopsScope && scopedShopIds.length > 0 ? (
              <p className="tko-home-section__sub">{t('platformHome.scopeSummary', { count: scopedShopIds.length })}</p>
            ) : null}
          </div>

          <div className="tko-home-kpi" role="list">
            <div className="tko-home-kpi__card" role="listitem">
              <span className="tko-home-kpi__label">{t('platformHome.spuPool')}</span>
              <strong className="tko-home-kpi__value">
                {loading && listingStats == null ? '—' : productTotal}
              </strong>
              <span className="tko-home-kpi__hint">
                {t('platformHome.onOffShelf', { listed: num(listingStats?.listed), offline: num(listingStats?.offline) })}
              </span>
            </div>
            <div className="tko-home-kpi__card" role="listitem">
              <span className="tko-home-kpi__label">{t('platformHome.draftsBox')}</span>
              <strong className="tko-home-kpi__value">{num(listingStats?.drafts)}</strong>
              <span className="tko-home-kpi__hint">{t('platformHome.productDrafts')}</span>
            </div>
            <div className="tko-home-kpi__card" role="listitem">
              <span className="tko-home-kpi__label">{t('platformHome.tiktok.priceEntry')}</span>
              <strong className="tko-home-kpi__value">{t('platformHome.tiktok.productInfo')}</strong>
              <span className="tko-home-kpi__hint">{t('platformHome.tiktok.priceHint')}</span>
            </div>
          </div>

          <div className="tko-home-analysis-grid">
            <div className="tko-home-panel">
              <h3 className="tko-home-panel__title">{t('platformHome.structure')}</h3>
              <div className="tko-home-bars">
                {ANALYSIS_PRODUCT_METRICS.map((metric) => (
                  <AnalysisBar
                    key={metric.key}
                    label={t(metric.labelKey)}
                    value={num(listingStats?.[metric.key])}
                    max={productMax}
                    tone={metric.tone}
                    disabled={!listReady}
                    onClick={() => goProductMetric(metric)}
                  />
                ))}
              </div>
            </div>
            <div className="tko-home-panel">
              <h3 className="tko-home-panel__title">{t('platformHome.tiktok.todos')}</h3>
              <ul className="tko-home-breakdown">
                <li>
                  <button type="button" className="tko-home-linkish" onClick={() => navigate(buildTiktokPath('orders'))}>
                    {t('platformHome.tiktok.awaitingShip')}
                  </button>
                  <strong>{num(todos.awaitingShipment)}</strong>
                </li>
                <li>
                  <button type="button" className="tko-home-linkish" onClick={() => navigate(buildTiktokPath('orders/returns'))}>
                    {t('platformHome.tiktok.pendingReturns')}
                  </button>
                  <strong>{num(todos.pendingReturns)}</strong>
                </li>
                <li>
                  <button type="button" className="tko-home-linkish" onClick={() => navigate(buildTiktokChildPath('compliance', 'materials'))}>
                    {t('platformHome.tiktok.manufacturer')}
                  </button>
                  <strong>—</strong>
                </li>
                <li>
                  <span>{t('platformHome.tiktok.reviewFailed')}</span>
                  <strong>{num(listingStats?.offline)}</strong>
                </li>
              </ul>
            </div>
            <div className="tko-home-panel">
              <h3 className="tko-home-panel__title">{t('platformHome.shortcuts')}</h3>
              <ul className="tko-home-breakdown">
                <li>
                  <button
                    type="button"
                    className="tko-home-linkish"
                    onClick={() => navigate(buildTiktokProductChildPath(''))}
                    disabled={!listReady}
                  >
                    {t('platformHome.tiktok.productInfo')}
                  </button>
                  <strong>—</strong>
                </li>
                <li>
                  <button
                    type="button"
                    className="tko-home-linkish"
                    onClick={() => navigate(buildTiktokProductChildPath('records'))}
                    disabled={!listReady}
                  >
                    {t('platformHome.tiktok.publishRecords')}
                  </button>
                  <strong>—</strong>
                </li>
                <li>
                  <button
                    type="button"
                    className="tko-home-linkish"
                    onClick={() => navigate(buildTiktokPath('marketing'))}
                    disabled={!listReady}
                  >
                    {t('platformHome.campaigns')}
                  </button>
                  <strong>—</strong>
                </li>
                {isFull ? (
                  <li>
                    <button
                      type="button"
                      className="tko-home-linkish"
                      onClick={() => navigate(buildTiktokChildPath('orders', 'stocking'))}
                      disabled={!listReady}
                    >
                      {t('platformHome.stockShip')}
                    </button>
                    <strong>—</strong>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        </section>

        {!listReady ? (
          <p className="tko-empty-hint">{t('platformHome.needShop', { platform: 'TikTok' })}</p>
        ) : null}
      </div>
    </ProductListCard>
  );
}
